import React, { useState, useEffect, lazy, Suspense, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Link,
  Element,
  Events,
  animateScroll as scroll,
  scrollSpy,
  scroller,
} from 'react-scroll';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import Menu from './Menu';
import camera from './img/camera.png'
import logo from './img/logo_new.png';
import foregroundlogo from './img/foreground.png';
import Unity from './img/Unity.jpg';
import Humanity from './img/Humanity.jpg';
import Love from './img/love3.jpg';
import Love2 from './img/love4.jpg';
import Community from './img/Community.jpg';
import Sustainability from './img/Sustainability.jpg';
import Peace from './img/peace.jpg';
import Respect from './img/Respect.jpg';
import Unity2 from './img/Unity2.jpg';
import unity from './img/unity.png';
import Humanity2 from './img/Humanity2.jpg';
import Community2 from './img/Community2.jpg';
import Sustainability2 from './img/Sustainability.jpg';
import Peace2 from './img/peace.png';
import Respect2 from './img/Respect.jpg';
import worldMap from './img/world.png';
// Additional images for programmes
import humanityPng from './img/humanity.png';
import commPng from './img/comm.png';
import peace2Png from './img/peace2.png';
import respectPng from './img/respect.png';
import HorizontalVideo from './img/horizontal_new2.mp4';
import VerticalVideo from './img/vertical_new2.mp4';
import Team from './Team';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import CookieConsent from "./CookieConsent";
import Principles from './Principles';
import Footer from './Footer';
import PrivacyPolicy from './PrivacyPolicy';
import LanguageSelector from './components/LanguageSelector';
import SEO from './components/SEO';
import LazyImage from './components/LazyImage';
import { trackDonationConversion } from './utils/tracking';
import './css/DonateButton.css'

const ContactForm = React.lazy(() => import('./Contact'));
const BeAPart = React.lazy(() => import('./BeAPart'));
const Projects = React.lazy(() => import('./Projects'));
const Campaigns = React.lazy(() => import('./Campaigns'));

const programmes = [
  {
    title: 'Love',
    text: 'We embrace love as the most transformative force in life',
    text2: 'We build bridges where walls stand, using the power of love to enhance consciousness and understanding',
    image: humanityPng,
  },
  {
    title: 'Unity',
    text: 'We trust in the communion of human being with the natural world',
    text2: 'Creating a bond and honoring her as our home',
    image: unity,
  },
  {
    title: 'Community',
    text: 'We seek to tend others in a selfless and unconditional manner',
    text2:
      'Our role is to amplify their vision by co-creating value chains. Rather than simply delivering aid and leaving, we help build replicable, self-sustaining systems that strengthen their own ecosystem',
    image: commPng,
  },
  {
    title: 'Harmony',
    text: 'We exude harmony and understand in our relationships',
    text2:
      'Advocating for a peaceful middle ground as a resolution for conflicts',
    image: peace2Png,
  },
  {
    title: 'Consciousness',
    text: 'We see others as we see ourselves',
    text2:
      'We treat others with respect and kindness, regardless of nationality, religion, status, gender, orientation, age, or any other factor.',
    image: respectPng,
  },
];

function AppContent() {
  const { t, tHtml } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentMenu, setCurrentMenu] = useState('main');
  const handleMenuToggle = () => { setIsMenuOpen(!isMenuOpen); };
  const aboutRef = useRef(null);
  const projectsRef = useRef(null);
  const campaignsRef = useRef(null);
  const principlesRef = useRef(null);
  const bePartRef = useRef(null);
  const contactRef = useRef(null);
  const heroVideoRef = useRef(null);  // TASK 2: ref for autoplay play() call
  const [currentCampaign, setCurrentCampaign] = useState(0);
  const [campaigns, setCampaigns] = useState([]);

  // Mapping of URL paths to section refs and scroll positions
  // Handles both language-prefixed paths (e.g., /en/about) and non-prefixed paths
  const getSectionFromPath = (path) => {
    // Remove language prefix if present
    // Special handling for "campaigns" path to avoid removing "ca" from it
    let cleanPath = path;
    
    // If the path contains "campaigns", temporarily replace it with a placeholder
    if (path.includes('campaigns')) {
      cleanPath = path.replace(/campaigns/g, 'CAMPAIGNS_PLACEHOLDER');
    }
    
    // Remove language prefix (only if it's a complete segment: /en/, /fr/, etc.)
    cleanPath = cleanPath.replace(/^\/(en|fr|es|ca|de)(\/|$)/, '$2') || '/';
    
    // Restore "campaigns" from placeholder
    if (cleanPath.includes('CAMPAIGNS_PLACEHOLDER')) {
      cleanPath = cleanPath.replace(/CAMPAIGNS_PLACEHOLDER/g, 'campaigns');
    }
    
    // Ensure cleanPath starts with '/' if it's not empty
    if (cleanPath !== '/' && !cleanPath.startsWith('/')) {
      cleanPath = '/' + cleanPath;
    }
  
    
    const sectionMap = {
      '/about': { ref: aboutRef, position: 1.5 },
      '/projects': { ref: projectsRef, position: 4 },
      '/campaigns': { ref: campaignsRef, position: 6 },
      '/principles': { ref: principlesRef, position: 8 },
      '/join': { ref: bePartRef, position: 12.5 },
      '/contact': { ref: contactRef, position: 16 },
      '/member': { ref: aboutRef, position: 1.5 }, // Alias for about section where team is shown
      '/privacy': { ref: null, position: 0 }, // Privacy policy page
      '/': { ref: null, position: 0 } // Home page
    };
    
    return sectionMap[cleanPath] || sectionMap['/'];
  };


  const handleNavigate = (menu) => { 
    // Prevent rapid successive clicks (minimum 500ms between navigations)
    const now = Date.now();
    if (now - lastClickTime < 200) return;
    setLastClickTime(now);
    

    const menuId = menu.toLowerCase();
    const sectionPositions = {
      'about': windowHeight * 1.5,
      'projects': windowHeight * 4,
      'campaigns': windowHeight * 6,
      'principles': windowHeight * 8,
      'beapart': windowHeight * 12.5,
      'contact': windowHeight * 16
    };

    // Map menu to path
    const pathMap = {
      'about': '/about',
      'projects': '/projects',
      'campaigns': '/campaigns',
      'principles': '/principles',
      'beapart': '/join',
      'contact': '/contact'
    };

    if (sectionPositions[menuId]) {
      // Update URL path
      if (pathMap[menuId]) {
        navigate(pathMap[menuId]);
      }

      // Temporarily force the section to be fully visible
      const targetRef = {
        'about': aboutRef,
        'projects': projectsRef,
        'campaigns': campaignsRef,
        'principles': principlesRef,
        'beapart': bePartRef,
        'contact': contactRef
      }[menuId];

      if (targetRef.current) {
        targetRef.current.style.opacity = 1;
        targetRef.current.style.pointerEvents = 'auto';
        targetRef.current.style.display = 'block';
        
        // Add a data attribute to mark this as a programmatic scroll
        targetRef.current.dataset.programmaticScroll = 'true';
      }

      window.scrollTo({
        top: sectionPositions[menuId],
        behavior: 'smooth'
      });

      // Re-enable scroll-based opacity control after scroll completes
      setTimeout(() => {
        if (targetRef.current) {
          targetRef.current.style.opacity = '';
          targetRef.current.style.pointerEvents = '';
          targetRef.current.style.display = '';
          delete targetRef.current.dataset.programmaticScroll;
        }
      }, 200); // Slightly longer delay to ensure smooth scroll completes
    }
  };
  const handleBack = () => { setCurrentMenu('main');};
  // Essential images (above-the-fold, critical for first paint)
  const essentialImages = [logo, foregroundlogo, camera];
  // Lazy images loaded BEFORE setIsLoaded(true) so the loader stays
  // at 100% only while ALL images are actually ready.
  // (Phillip's symptom: images arriving after loader hit 100%)
  const lazyImages = [
    Unity, Unity2, Love, Love, Community, Community2,
    Sustainability, Peace, Respect, worldMap,
    // Programme images
    humanityPng, unity, commPng, peace2Png, respectPng,
    // Background array images
    Love2, Peace2, Respect2, Humanity, Humanity2, Sustainability2
  ];
  // Combined preload pool: loader dismisses as soon as ESSENTIAL images are cached.
  // lazyImages load on-demand via IntersectionObserver (LazyImage component),
  // NOT during the preload phase — this is the 70% video + essential-only pattern.
  const allPreloadImages = [...essentialImages];
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [lazyImagesLoaded, setLazyImagesLoaded] = useState(false);
  const [backgroundScale, setBackgroundScale] = useState(1);
  const [foregroundScale, setForegroundScale] = useState(1);
  const [backgroundTranslate, setBackgroundTranslate] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const logoBackgrounds = [Love,Unity , Community, Peace, Respect];
  const logoBackgrounds2 = [Love2,Unity2, Community2, Peace2, Respect2];
  const [currentBackgroundArray, setCurrentBackgroundArray] = useState(logoBackgrounds);
  const [currentProgram, setCurrentProgram] = useState(0);
  const [showTeam, setShowTeam] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);

  const [scrollY, setScrollY] = useState(0);
  const windowHeight = window.innerHeight;


  const [packageStates, setPackageStates] = useState([
    { visible: true, expanded: true },   // First package - visible and expanded
    { visible: false, expanded: false }, // Second package - hidden initially
    { visible: false, expanded: false }  // Third package - hidden initially
  ]);
  const [showDonatePopup, setShowDonatePopup] = useState(false); // New state for popup
  const [donationAmount, setDonationAmount] = useState(''); // New state for amount
  const [donationMessage, setDonationMessage] = useState(''); // Message for PayPal
  const [donorEmail, setDonorEmail] = useState(''); // New state for donor email
  const [selectedCampaign, setSelectedCampaign] = useState(null); // Track selected campaign
  const [donationSuccess, setDonationSuccess] = useState(false); // Track donation success state
  const [contactForm, setContactForm] = useState({
    name: '',
    lastname: '',
    email: '',
    subject: '',
    message: '',
  });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [activeSection, setActiveSection] = useState('home');

  const scrollToMainContent = () => {
    window.scrollTo({
      top: window.innerHeight+200,
      behavior: 'smooth'
    });
  };
useEffect(() => {
  let loadedCount = 0;
  // We track video + ALL preload images (essential + lazy) so the
  // loader progress bar reflects the true image-readiness state.
  const totalToLoad = allPreloadImages.length + 1; // +1 for video
  let isMounted = true;
  let videoLoaded = false;
  let video70Ready = false;  // TASK 1: 70% buffer threshold

  const updateProgress = () => {
    if (!isMounted) return;
    const progress = Math.round((loadedCount / totalToLoad) * 100);
    setLoadingProgress(progress);

    // TASK 2 (lazy-FIX): The loader only dismisses when BOTH conditions hold:
    //   (a) the video is ready (fully loaded OR 70% buffered per TASK 1), AND
    //   (b) ALL preload images are cached.
    // This prevents Phillip's symptom ("loader at 100% but images still missing"):
    // previously the `|| video70Ready` alone could dismiss the loader while
    // images were still in flight. We now require the essential images
    // (logo, foregroundlogo, camera) at minimum before allowing the 70%-video
    // early-out, and full image completion otherwise.
    const essentialDone = loadedCount >= essentialImages.length;
    if ((loadedCount === totalToLoad) || (video70Ready && essentialDone)) {
      setTimeout(() => {
        if (isMounted) {
          setIsLoaded(true);

          // TASK 2 (lazy-images fix): Broadcast a global event so every
          // LazyImage component (which is listening for `yourngo:loaded`) flips
          // its src from null → real URL simultaneously. This matches the
          // Lumenya/Glow pattern: once the site is shown, everything below
          // the fold is loaded in one shot instead of one-at-a-time on scroll.
          window.__yourngoLoaded = true;
          window.dispatchEvent(new Event('yourngo:loaded'));

          // Mark the lazy-images flag for any non-event listeners.
          setLazyImagesLoaded(true);
          
          // After loading completes, check if there's a path in the URL and scroll to that section
          // Only scroll if we're not on the home page (position > 0)
          const path = location.pathname;
          const section = getSectionFromPath(path);
          if (section && section.position !== undefined && section.position > 0) {
            const { position } = section;
            const targetScrollY = window.innerHeight * position;

            // Small delay to ensure everything is rendered before scrolling
            setTimeout(() => {
              window.scrollTo({
                top: targetScrollY,
                behavior: 'smooth'
              });
            }, 300);
          }
        }
      }, 500);
    }
  };

  const handleEssentialImageLoad = () => {
    if (!isMounted) return;
    loadedCount += 1;
    updateProgress();
  };

  // Load ALL preload images (essential + lazy) up front.
  // This fixes Phillip's symptom where images arrived AFTER the loader
  // hit 100%: the loader now stays until every image is actually cached.
  allPreloadImages.forEach((src) => {
    const img = new Image();
    img.onload = handleEssentialImageLoad;
    img.onerror = handleEssentialImageLoad;
    img.src = src;
  });

  // Track video loading - we'll track when the actual video element loads
  // We don't create a separate video element, we'll just track the actual one
  const handleVideoLoad = () => {
    if (!isMounted || videoLoaded) return;
    videoLoaded = true;
    loadedCount += 1;
    // TASK 3: Force play() on full-load path as well, so autoplay is
    // guaranteed whether we hit the 70% threshold or the canplay event.
    const v = heroVideoRef.current;
    if (v && v.paused) {
      v.play().catch(() => {});
    }
    updateProgress();
  };

  // TASK 1: 70%-buffer handler. Called from the <video> onProgress/onTimeUpdate.
  // When video.buffered.end(0) / duration >= 0.7 → flag video70Ready and trigger
  // updateProgress() which will setIsLoaded(true) immediately (even if images
  // are still loading). This matches Lumenya/glow on-the-fly loading pattern.
  const handleVideoProgress = (videoEl) => {
    if (!isMounted || video70Ready) return;
    try {
      if (videoEl.buffered.length > 0 && videoEl.duration > 0) {
        const bufferedFraction = videoEl.buffered.end(0) / videoEl.duration;
        if (bufferedFraction >= 0.7) {
          video70Ready = true;
          // Reflect 70%+ in the counter
          setLoadingProgress(Math.max(loadingProgress, 70));
          updateProgress();
          // TASK 3 (autoplay fix): Trigger play() at the same moment we
          // decide the site is showable. autoPlay attribute alone is not
          // reliable on Vivaldi / iOS / StrictMode. Calling play() inside
          // the same synchronous tick as the 70% threshold avoids the race
          // where the loader hides but the video never starts.
          if (videoEl && videoEl.paused) {
            videoEl.play().catch(() => {});
          }
        }
      }
    } catch (e) {
      // buffered.end() can throw if ranges changed — ignore silently
    }
  };
  
  // We'll attach this handler to the actual video element via ref
  // Store the handlers globally so the video element can access them
  window.__videoLoadHandler = handleVideoLoad;
  window.__videoProgressHandler = handleVideoProgress;
  
  // Also set a timeout to show the site even if video takes too long to buffer
  // This ensures the site shows quickly while video continues loading in background
  const videoBufferTimer = setTimeout(() => {
    if (!videoLoaded && isMounted) {
      handleVideoLoad();
    }
  }, 3000); // 3 second timeout for initial buffering (video gets more time)

  // Function to start loading lazy images in background
  const startLazyImageLoading = () => {
    if (!isMounted) return;
    
    let lazyLoadedCount = 0;
    const totalLazyImages = lazyImages.length;
    
    const handleLazyImageLoad = () => {
      if (!isMounted) return;
      lazyLoadedCount += 1;
      
      // Update overall progress (essential + lazy)
      const totalProgress = Math.round(((totalToLoad + lazyLoadedCount) / (totalToLoad + totalLazyImages)) * 100);
      setLoadingProgress(totalProgress);
      
      if (lazyLoadedCount === totalLazyImages) {
        setLazyImagesLoaded(true);
      }
    };
    
    lazyImages.forEach((src) => {
      const img = new Image();
      img.onload = handleLazyImageLoad;
      img.onerror = handleLazyImageLoad;
      img.src = src;
    });
  };

  return () => {
    isMounted = false;
    // Clean up the global handlers
    delete window.__videoLoadHandler;
    delete window.__videoProgressHandler;
    // Clear the video buffer timer
    if (videoBufferTimer) {
      clearTimeout(videoBufferTimer);
    }
  };
}, [location, windowWidth]);

  // TASK 2: Hero video autoplay — call play() after mount with Promise handling.
  // Lesson 4 (Lumenya): play() returns a Promise; catch AbortError race-condition
  // when the browser pauses to buffer. muted+playsInline satisfies autoplay policy.
  useEffect(() => {
    const v = heroVideoRef.current;
    if (!v) return;
    // Attempt to play immediately; retry on canplay as backup
    const tryPlay = () => {
      if (v.paused) {
        v.play().catch(() => {
          // Autoplay blocked or race-condition — retry on next user gesture is
          // handled by the muted+playsInline attributes; ignore AbortError here.
        });
      }
    };
    tryPlay();
    v.addEventListener('canplay', tryPlay, { once: true });
    return () => v.removeEventListener('canplay', tryPlay);
  }, [isLoaded]);

  useEffect(() => {
    // Handle about section selection from menu
    const handleAboutSectionSelect = (event) => {
      const { section } = event.detail;
      
      // Show team and scroll to the specific section
      setShowTeam(true);
      
      // Use setTimeout to ensure the team component is rendered before scrolling
      setTimeout(() => {
        const windowHeight = window.innerHeight;
        const targetScrollY = windowHeight * section;
        window.scrollTo({
          top: targetScrollY,
          behavior: 'smooth'
        });
        
      }, 100);
    };

    window.addEventListener('aboutSectionSelect', handleAboutSectionSelect);
    
    const unifiedScrollHandler = () => {

      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Calculate section progress (0-1 for each section)
      const parallaxProgress = Math.min(scrollY / windowHeight, 1);
      
      // About section - appears after parallax, stays visible for 3 window heights
      const aboutProgress = Math.max(0, Math.min((scrollY - windowHeight) / (windowHeight * 0.3), 1));
      
      // Projects section - appears after about, stays visible for 3 window heights  
      const projectsProgress = Math.max(0, Math.min((scrollY - windowHeight * 3) / (windowHeight * 0.3), 1));
      
      // Campaigns section - appears after projects, stays visible for 3 window heights
      const campaignsProgress = Math.max(0, Math.min((scrollY - windowHeight * 5) / (windowHeight * 0.3), 1));
      
      // Principles section - appears after campaigns, stays visible for 4 window heights
      const principlesProgress = Math.max(0, Math.min((scrollY - windowHeight * 7) / (windowHeight * 0.3), 1));
      
      // bePart section - appears after principles, stays visible for 2 window heights
      const beaPartProgress = Math.max(0, Math.min((scrollY - windowHeight * 11.5) / (windowHeight * 0.3), 1));
      
      // Contact section - appears after bePart, stays visible for 1 window height
      const contactProgress = Math.max(0, Math.min((scrollY - windowHeight * 15) / (windowHeight * 0.5), 1));

      // Control reCAPTCHA badge visibility based on contact section visibility
      const recaptchaBadge = document.querySelector('.grecaptcha-badge');
      if (recaptchaBadge) {
        if (contactProgress > 0.1) {
          recaptchaBadge.style.visibility = 'visible';
        } else {
          recaptchaBadge.style.visibility = 'hidden';
        }
      }

      // Update package states based on screen width
      if (window.innerWidth > 1000) {
        // On large screens, show all packages simultaneously and expanded
        const newPackageStates = [
          { visible: true, expanded: true },
          { visible: true, expanded: true },
          { visible: true, expanded: true }
        ];
        setPackageStates(newPackageStates);
      } else if (beaPartProgress > 0) {
        // On smaller screens, use the original scroll-based logic only when in bePart section
        const sectionHeight = windowHeight * 2; // Height of bePart section
        const scrollPosition = scrollY - (windowHeight * 11.5); // Relative to section start
        console.log(scrollPosition);
        console.log('sectionheight:' + sectionHeight * 0.7)
        const newPackageStates = [
          { 
            visible: true,
            expanded: scrollPosition <= sectionHeight * 0.7
          },
          { 
            visible: scrollPosition >= sectionHeight * 0.7,
            expanded: scrollPosition >= sectionHeight * 0.7 && 
                     scrollPosition <= sectionHeight * 1.2
          },
          { 
            visible: scrollPosition >= sectionHeight * 1.2,
            expanded: scrollPosition >= sectionHeight * 1.2
          }
        ];
        setPackageStates(newPackageStates);
      }

      // Update scroll position state
      setScrollY(scrollY);

      // Update active section based on scroll position
      // Only update if not in Team view
      if (!showTeam) {
        if (scrollY < windowHeight * 1) {
          setActiveSection('home');
        } else if (scrollY < windowHeight * 3) {
          setActiveSection('about');
        } else if (scrollY < windowHeight * 5) {
          setActiveSection('projects');
        } else if (scrollY < windowHeight * 7) {
          setActiveSection('campaigns');
        } else if (scrollY < windowHeight * 11.5) {
          setActiveSection('principles');
        } else if (scrollY < windowHeight * 15) {
          setActiveSection('beapart');
        } else {
          setActiveSection('contact');
        }
      }

      // Parallax effects
      setBackgroundTranslate(parallaxProgress * -50);
      setBackgroundScale(1 + parallaxProgress * 0.2);
      setForegroundScale(1 + parallaxProgress * 0.1);
      setOpacity(1 - parallaxProgress);
      
      if (aboutRef.current) {
        aboutRef.current.style.opacity = 0;
        aboutRef.current.style.pointerEvents = 'none';
        aboutRef.current.style.display = 'none';
      }
      if (projectsRef.current) {
        projectsRef.current.style.opacity = 0;
        projectsRef.current.style.pointerEvents = 'none';
        projectsRef.current.style.display = 'none';
      }
      if (campaignsRef.current) {
        campaignsRef.current.style.opacity = 0;
        campaignsRef.current.style.pointerEvents = 'none';
        campaignsRef.current.style.display = 'none';
      }
      if (principlesRef.current) {
        principlesRef.current.style.opacity = 0;
        principlesRef.current.style.pointerEvents = 'none';
        principlesRef.current.style.display = 'none';
      }
      if (bePartRef.current) {
        bePartRef.current.style.opacity = 0;
        bePartRef.current.style.pointerEvents = 'none';
        bePartRef.current.style.display = 'none';
      }
      if (contactRef.current) {
        contactRef.current.style.opacity = 0;
        contactRef.current.style.pointerEvents = 'none';
        contactRef.current.style.display = 'none';
      }
      // Activate current section based on scroll position
      if (scrollY < windowHeight * 1) {
        // Parallax section
      } else if (scrollY < windowHeight * 3) {
        // About section
        if (aboutRef.current) {
          aboutRef.current.style.opacity = aboutProgress;
          aboutRef.current.style.pointerEvents = aboutProgress > 0.5 ? 'auto' : 'none';
          aboutRef.current.style.display = 'block';
        }
      } else if (scrollY < windowHeight * 5) {
        // Projects section
        if (projectsRef.current) {
          projectsRef.current.style.opacity = projectsProgress;
          projectsRef.current.style.pointerEvents = projectsProgress > 0.5 ? 'auto' : 'none';
          projectsRef.current.style.display = 'block';
        }
      } else if (scrollY < windowHeight * 7) {
        // Campaigns section
        if (campaignsRef.current) {
          campaignsRef.current.style.opacity = campaignsProgress;
          campaignsRef.current.style.pointerEvents = campaignsProgress > 0.5 ? 'auto' : 'none';
          campaignsRef.current.style.display = 'block';
        }
      } else if (scrollY < windowHeight * 11.5) {
        // Principles section
        if (principlesRef.current) {
          principlesRef.current.style.opacity = principlesProgress;
          principlesRef.current.style.pointerEvents = principlesProgress > 0.1 ? 'auto' : 'none';
          principlesRef.current.style.display = 'block';
          
          // Cycle through programs
          const programIndex = Math.floor((scrollY - windowHeight * 8.0) / (windowHeight * 0.5));
          if (programIndex >= 0 && programIndex < programmes.length) {
            setCurrentProgram(programIndex);
          }
        }
      } else if (scrollY < windowHeight * 15) {
        // BePart section
        if (bePartRef.current) {
          bePartRef.current.style.opacity = beaPartProgress;
          bePartRef.current.style.pointerEvents = beaPartProgress > 0.1 ? 'auto' : 'none';
          bePartRef.current.style.display = 'block';
        }
      } else {
        // Contact section
        if (contactRef.current) {
          contactRef.current.style.opacity = contactProgress;
          contactRef.current.style.pointerEvents = contactProgress > 0.5 ? 'auto' : 'none';
          contactRef.current.style.display = 'block';
        }
      }
      
    };

    window.addEventListener('scroll', unifiedScrollHandler, { passive: true });
    return () => {
      window.removeEventListener('scroll', unifiedScrollHandler);
      window.removeEventListener('aboutSectionSelect', handleAboutSectionSelect);
    };
  }, [packageStates, showTeam]);

  useEffect(() => {
    // Function to check the screen width and set the background array accordingly
    const updateBackgroundArray = () => {
      if (window.innerWidth > 1150) {
        setCurrentBackgroundArray(logoBackgrounds2);
      } else {
        setCurrentBackgroundArray(logoBackgrounds);
      }
    };

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      updateBackgroundArray();
    };

    // Initial check
    handleResize();

    // Add event listeners to handle window resize
    window.addEventListener('resize', handleResize);

    // Cleanup listeners on component unmount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCampaign(prev => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Handle URL path changes for direct section navigation
  useEffect(() => {
    const handlePathChange = () => {
      const path = location.pathname;
      const section = getSectionFromPath(path);
      
      if (section && section.position !== undefined && section.position > 0) {
        const { position } = section;
        
        // Wait for the page to be fully loaded and sections to be ready
        const scrollToSection = () => {
          const targetScrollY = window.innerHeight * position;
          
          window.scrollTo({
            top: targetScrollY,
            behavior: 'smooth'
          });
        };

        // If page is already loaded, scroll immediately
        if (document.readyState === 'complete') {
          setTimeout(scrollToSection, 100); // Small delay to ensure everything is ready
        } else {
          // Wait for page to load before scrolling
          window.addEventListener('load', () => {
            setTimeout(scrollToSection, 100);
          });
        }
      }
    };

    // Handle initial path on page load with a small delay
    setTimeout(handlePathChange, 300);

    // Also handle path changes when location changes
    handlePathChange();
    
    return () => {
      // Cleanup if needed
    };
  }, [location, navigate]);


  const handlePackageClick = (index) => {
    const windowHeight = window.innerHeight;
    let targetScrollY;
    
    // Calculate target positions based on package index
    switch(index) {
      case 0: // First package
        targetScrollY = windowHeight * 12; // Start of bePart section
        break;
      case 1: // Second package
        targetScrollY = windowHeight * 13; // Middle of bePart section
        break;
      case 2: // Third package
        targetScrollY = windowHeight * 14; // Scroll further to ensure full visibility
        break;
      default:
        targetScrollY = windowHeight * 11.5;
    }

    window.scrollTo({
      top: targetScrollY,
      behavior: 'smooth',
    });
  };

  const handleBackToMainContent = () => {
    setShowTeam(false);
    navigate('/');
    scroll.scrollTo(1000, { smooth: true });
  };

  const handleShowPrivacyPolicy = () => {
    // Reset scroll position to top when showing privacy policy
    window.scrollTo({ top: 0, behavior: 'instant' });
    setShowPrivacyPolicy(true);
    navigate('/privacy');
  };

  const handleBackFromPrivacyPolicy = () => {
    setShowPrivacyPolicy(false);
    navigate('/');
  };


  return (
    <PayPalScriptProvider
      options={{ 
        'client-id': 'AR41LY_sQYZWyLubeKEHdkGPF-QuFMa7XDSV3IXqBNxP0hmb7oaK15gSmlG_4NPLfST-hxEaTGD3kF1H',
        'vault': true,
        'components': 'buttons',
        'currency': 'EUR',
        'disable-funding': 'card,credit,venmo'
      }}
    >
    <div className="App">
      {/* Loading Overlay */}
      {!isLoaded && (
        <div className="loading-overlay">
          <div className="loading-screen">
            <div className="circle-wrapper">
              <img src={camera} alt="Logo" className="loading-logo" />
              <div className="loading-circle"></div>
            </div>
            <p className="loading-text">{loadingProgress}%</p>
          </div>
        </div>
      )}
      <SEO />
      <div className={`burger-menu ${
        isMenuOpen ? 'black-burger' : 
        (aboutRef.current?.style.opacity > 0.5 || 
         projectsRef.current?.style.opacity > 0.5 || 
         campaignsRef.current?.style.opacity > 0.5 ||
         bePartRef.current?.style.opacity > 0.5 || 
         contactRef.current?.style.opacity > 0.5 ||
         showPrivacyPolicy) ? 'black-burger' : 'white-burger'
      }`} onClick={handleMenuToggle}>
          <div className={`burger-line ${isMenuOpen ? 'open' : ''}`}></div>
          <div className={`burger-line ${isMenuOpen ? 'open' : ''}`}></div>
          <div className={`burger-line ${isMenuOpen ? 'open' : ''}`}></div>
        </div>
      <Menu 
        isOpen={isMenuOpen}
        currentMenu={currentMenu}
        onNavigate={handleNavigate}
        onBack={handleBack}
        activeSection={activeSection}
        setIsMenuOpen={setIsMenuOpen}
        setShowTeam={setShowTeam}
        setCurrentMenu={setCurrentMenu}
        setShowPrivacyPolicy={setShowPrivacyPolicy}
      />
      {showTeam ? (
        <Team onBack={handleBackToMainContent} onShowDonate={() => setShowDonatePopup(true)} />
      ) : showPrivacyPolicy ? (
        <PrivacyPolicy onBack={handleBackFromPrivacyPolicy} onShowDonate={() => setShowDonatePopup(true)} />
      ) : (
        <>
          {scrollY > windowHeight && (
            <div className="fixed-buttons-container">
              <button 
                className="donatefixed-button"
                onClick={() => setShowDonatePopup(true)}
              >
                {t('donation.button')}
              </button>
              <LanguageSelector />
            </div>
          )}
          {/* Main Scroll Container */}
          <div className="scroll-section" style={{
            height: '1800vh', 
            position: 'relative',
            zIndex: 1
          }}>
            {/* Parallax Section */}
            <div className="section-container parallax-section" style={{
              position: 'fixed',
              width: '100%',
              height: '100vh',
              zIndex: 1,
              opacity,
              pointerEvents: opacity > 0.5 ? 'auto' : 'none',
              willChange: 'opacity'
            }}>
              <div className="scroll-content">
                <div style={{
                  transform: `translateY(${backgroundTranslate}px) scale(${backgroundScale})`,
                  height: '100vh',
                  width: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  overflow: 'hidden'
                }}>
                  <video
                    ref={heroVideoRef}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                      width: '100vw',
                      height: '100vh',
                      minWidth: '100%',
                      minHeight: '100%',
                      objectFit: 'cover'
                    }}
                    onLoadedData={() => {
                      // Call the global video load handler if it exists
                      if (window.__videoLoadHandler) {
                        window.__videoLoadHandler();
                      }
                    }}
                    onCanPlay={(e) => {
                      // Also call on canplay for better compatibility
                      if (window.__videoLoadHandler) {
                        window.__videoLoadHandler();
                      }
                      // TASK 2: Force play() to bypass autoplay race-condition
                      const v = e.currentTarget;
                      if (v && v.paused) {
                        v.play().catch(() => {});
                      }
                    }}
                    onProgress={(e) => {
                      // TASK 1: Check 70% buffer threshold
                      if (window.__videoProgressHandler) {
                        window.__videoProgressHandler(e.currentTarget);
                      }
                    }}
                    onTimeUpdate={(e) => {
                      // TASK 1: Fallback 70% check (some browsers don't fire onProgress reliably)
                      if (window.__videoProgressHandler) {
                        window.__videoProgressHandler(e.currentTarget);
                      }
                    }}
                  >
                    <source src={windowWidth > 800 ? HorizontalVideo : VerticalVideo} type="video/mp4" />
                  </video>
                </div>
                <div style={{
                  transform: `scale(${foregroundScale})`,
                  height: '100vh'
                }} className="foreground-layer">
                                  <div className="foreground-circle" style={{
                  transform: `scale(${foregroundScale})`,
                }}/>
                  <img src={foregroundlogo} alt="Logo" className="foreground-logo" />
                </div>
                <div className="arrow-container">
                  <div className="circle-animation" onClick={scrollToMainContent}>
                    <i className="bi bi-arrow-down"></i>
                  </div>
                </div>
              </div>
            </div>

          </div>
            {/* About Section */}
              <div ref={aboutRef} className="about-section" style={{
              position: 'fixed',
              top: '0',
              width: '100%',
              minHeight: '200vh',
              opacity: 0,
              transition: 'opacity 0.5s ease',
              pointerEvents: 'none',
              zIndex: 2,
              willChange: 'opacity'
            }}>
                <div className="logo-container">
                  <LazyImage src={logo} alt="Logo" className="logo" />
                </div>
                <h2 className="title">{t('about.title')}</h2>
                <div className="title-content">
                  <p dangerouslySetInnerHTML={tHtml('about.description')}></p>
                </div>
                <button className="btn btn-outline-primary moreabout" onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'instant' });
                  setShowTeam(true);
                  navigate('/aboutus');
                }}>{t('about.moreAbout')}</button>
                <div className="scroll-space" style={{ height: '200vh' }}>
                  <div className="arrow-container">
                    <div className="circle-animation" onClick={scrollToMainContent}>
                      <i className="bi bi-arrow-down"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div ref={projectsRef} className="projects-section" style={{
                position: 'fixed',
                top: '20vh',
                width: '100%',
                minHeight: '300vh',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
                zIndex: 3,
                background: 'none'
              }}>
                <Suspense fallback={null}>
                  <Projects />
                </Suspense>
              </div>
            {/* Campaigns Section */}
              <div ref={campaignsRef} className="campaigns-section" style={{
                position: 'fixed',
                top: '8vh',
                width: '100%',
                minHeight: '300vh',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
                zIndex: 3,
                background: 'none'
              }}>
                <Suspense fallback={null}>
                  <Campaigns />
                </Suspense>
              </div>
            {/* Principles Section*/}
                <div ref={principlesRef} className="principles-section" style={{
                position: 'fixed',
                top: '0',
                width: '100%',
                minHeight: '300vh',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
                zIndex: 4,
              }}>
                  <Principles 
                    currentProgram={currentProgram}
                    currentBackgroundArray={currentBackgroundArray}
                    programmes={programmes}
                    onNavigate={handleNavigate}
                  />
                  <div className="scroll-space" style={{ height: '100vh' }}>
                    {currentProgram === programmes.length - 1 && (
                      <div className="arrow-container">
                        <div className="circle-animation" onClick={scrollToMainContent}>
                          <i className="bi bi-arrow-down"></i>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="arrow-container">
                    <div className="circle-animation" onClick={scrollToMainContent}>
                      <i className="bi bi-arrow-down"></i>
                    </div>
                  </div>
                </div>
                <div ref={bePartRef} className="beapart-section" style={{
                position: 'fixed',
                top: '0',
                width: '100%',
                minHeight: '300vh',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
                zIndex: 4,
              }}>
                  <Suspense fallback={null}>
                    <BeAPart 
                      packageStates={packageStates}
                      handlePackageClick={handlePackageClick}
                    />
                  </Suspense>
                </div>
            <div ref={contactRef} className="contact-section" style={{
                position: 'fixed',
                top: '0',
                width: '100%',
                minHeight: '100vh',
                opacity: 0,
                transition: 'opacity 0.5s ease',
                pointerEvents: 'none',
                zIndex: 5,
                backgroundColor: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <Suspense fallback={null}>
                <ContactForm />
              </Suspense>
              <Footer onPrivacyPolicyClick={handleShowPrivacyPolicy} />
            </div>
        </>
      )}
      {showDonatePopup && (
          <div className="donate-popup">
              <div className="donate-popup-content">
                <h2>{t('donation.title')}</h2>
                {selectedCampaign && (
                  <h3 className="campaign-title">
                    {t('donation.supporting', { campaign: selectedCampaign.title })}
                  </h3>
                )}
                <button
                  className="close-button"
                  onClick={() => {
                    setShowDonatePopup(false);
                    setSelectedCampaign(null);
                    setDonationAmount('');
                    setDonationMessage('');
                    setDonorEmail('');
                  }}
                >
                  &times;
                </button>
              {donationSuccess ? (
                <div className="donation-success">
                  <div className="success-icon" style={{
                    width: '15vh',
                    margin: '0 auto',
                    display: 'flex',
                    justifyContent: 'center'
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#4CAF50"/>
                    </svg>
                  </div>
                  <h3>{t('donation.success.title')}</h3>
                  <p>{t('donation.success.message')}</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowDonatePopup(false);
                      setDonationSuccess(false);
                      setDonationAmount('');
                      setDonationMessage('');
                      setSelectedCampaign(null);
                    }}
                  >
                    {t('donation.success.close')}
                  </button>
                </div>
              ) : (
                <div className="donation-form">
                  <div className="form-group">
                    <label htmlFor="donationAmount">{t('donation.amount')}</label>
                    <input
                      type="number"
                      id="donationAmount"
                      className="form-control"
                      value={donationAmount}
                      onChange={(e) => {
                        const value = e.target.value;
                        const parsedValue = value === '' ? '' : parseFloat(value);
                        setDonationAmount(parsedValue);
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value && !isNaN(value)) {
                          setDonationAmount(parseFloat(value));
                        }
                      }}
                      min="1"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="donationMessage">{t('donation.message')}</label>
                    <textarea
                      id="donationMessage"
                      className="form-control"
                      value={donationMessage}
                      onChange={(e) => setDonationMessage(e.target.value)}
                      rows="3"
                    />
                  </div>
                  {/* PayPal Buttons */}
                  {donationAmount ? (
                    <PayPalButtons
                      style={{ layout: 'vertical' }}
                      forceReRender={[donationAmount, selectedCampaign]}
                      createOrder={async (data, actions) => {
                      return actions.order.create({
                        purchase_units: [{
                          amount: {
                            value: donationAmount.toString(),
                            currency_code: 'EUR'
                          },
                          description: donationMessage
                        }]
                      });
                    }}
                      onApprove={async (data, actions) => {
                      const details = await actions.order.capture();
                      try {
                        const response = await axios.post('https://ngo.phnx.dev/donation_handler.php', {
                          transaction_id: details.id,
                          campaignId: selectedCampaign?.id,
                          donor_email: details.payer?.email_address || 'no-email@example.com',
                          amount: donationAmount,
                          payment_status: 'completed'
                        }, {
                          headers: {
                            'X-API-Key': 'DONATION_4f7a9b2c8d3e1f6a5c9b8d7e2f3a4c6b'
                          }
                        });

                        if (response.data.success) {
                          setCampaigns(prevCampaigns => 
                            prevCampaigns.map(campaign => 
                              campaign.id === selectedCampaign.id 
                                ? { 
                                    ...campaign, 
                                    progress: response.data.newProgress 
                                  } 
                                : campaign
                            )
                          );
                          
                          // Track Google Ads conversion with Enhanced Conversions
                          trackDonationConversion(donationAmount, 'EUR', details.id, details.payer?.email_address);
                          
                          // Show success state in popup
                          setDonationSuccess(true);
                        } else {
                          throw new Error('Failed to update donation');
                        }
                      } catch (error) {
                        console.error('Donation update failed:', error);
                        alert(t('donation.errors.paymentProcessedFailed'));
                      }
                    }}
                    onError={(err) => {
                      console.error('PayPal error:', err);
                      alert(t('donation.errors.paymentProcessingFailed'));
                      setDonationAmount(''); // Reset amount on error
                    }}
                  />
                ) : (
                  <p className="text-danger">{t('donation.enterAmount')}</p>
                )}
              </div>
              )}
            </div>
          </div>
        )}
      </div>
      <CookieConsent />
      </PayPalScriptProvider>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

export default App;